import { Flex, layout, styled, Label } from '@fareye/ui'
import {
  pascalCase,
  toTileCase,
  getValueFromNestedKey,
  isEmpty,
  formatMs
} from '@fareye/utils'
import { IColumn } from '../../utility/types'
import MultiModelStoryLine from '../../components/MultiModelStoryLine'
import { Span, Link } from '../../components/Timeline/styled'
import { formatters } from '../../config/constants'
import React from 'react'
import { displayTime } from '../Timeline/function'
import { ColorSpan } from '../../style/styled'

interface IHeader {
  align?: 'left' | 'right' | 'center'
}

// const Fa = {
//   FaBatteryEmpty,
//   FaBatteryFull,
//   FaBatteryQuarter,
//   FaBatteryThreeQuarters
// }

const ColFullHeight = styled((props: any) => (
  <Flex
    p={2}
    justifyContentSpaceEvenly
    column
    {...props}
    style={{
      height: '100%',
      ...props.style
    }}
  >
    {props.children}
  </Flex>
))`
  ${layout}
`
const FullHeight = styled((props: any) => (
  <Flex
    p={2}
    alignItemsCenter
    textCenter
    {...props}
    style={{ height: '100%', ...props.style }}
  >
    {props.children}
  </Flex>
))`
  ${layout}
`

const Header: any = styled(Span)`
  width: 100%;
  display: block;
  text-transform: uppercase;
  padding: 0 6px;
  text-align: ${({ align = 'left' }: IHeader) => align};
`

function generateColumnObject(
  column: IColumn,
  { t, links, queryParams = {} }: any
) {
  const { label, key, formatter, columnConfig } = column
  switch (key) {
    case 'expand':
      return {
        Header: () => (
          <Header data-id={key} style={{ display: 'none' }}></Header>
        ),
        id: 'expand',
        expander: true,
        width: 0,
        minWidth: 0,
        style: { padding: 0, display: 'none' },
        Expander: () => null
      }
    case 'exceptions':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        sortable: false,
        minWidth: 80,
        Cell: ({ row: { original } }: any) => (
          <FullHeight justifyContentCenter>
            <Label
              variant={`${original.exceptionCount ? 'danger' : 'secondary'}`}
            >
              {original.exceptionCount || 0}
            </Label>
          </FullHeight>
        )
      }
    case 'transporterDetails':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        minWidth: 200,
        sortable: false,
        Cell: ({ row: { original } }: any) =>
          original.transporterName && (
            <FullHeight style={{ background: 'rgba(230,233,236,0.5)' }}>
              <Flex column style={{ textAlign: 'left', flexGrow: 1 }}>
                {original.transportMode === 'ocean' && (
                  <>
                    <Span variant="primary">{original.vesselName}</Span>
                    <Span variant="secondary">{original.vesselNo}</Span>
                  </>
                )}
                {original.transportMode !== 'ocean' && (
                  <>
                    <Span variant="primary">{original.driverName}</Span>
                    <Span variant="secondary">{original.vehicleNo}</Span>
                  </>
                )}
                <Label
                  variant="secondary ellipsis"
                  style={{ fontWeight: 600, maxWidth: '250px' }}
                >
                  {original.transporterName}
                </Label>
              </Flex>
            </FullHeight>
          )
      }
    case 'transitTime':
      return {
        Header: () => <Header>{t(label)}</Header>,
        id: key,
        sortable: false,
        minWidth: 120,
        Cell: ({ row: { original } }: any) => {
          return (
            <ColFullHeight>
              <ColorSpan variant="h6" title={original.status.replace(' ', '')}>
                {original.status
                  ? `${t(pascalCase(original.status).trim())} ${
                      original.timeDifference
                        ? `${t('by')} ${formatMs(original.timeDifference)}`
                        : ''
                    }`
                  : ''}
              </ColorSpan>
              <Span mb={1} color="#727272">
                {`${t('Target')} - ${formatMs(Number(original.targetTime))}`}
              </Span>
              <Span mb={1} color="#727272">
                {`${t('Actual')} - ${formatMs(
                  Number(original.actualTransitTime)
                )}`}
              </Span>
            </ColFullHeight>
          )
        }
      }

    case 'packageTransporterDetails':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        minWidth: 200,
        sortable: false,
        Cell: ({ row: { original } }: any) => (
          <FullHeight style={{ background: 'rgba(230,233,236,0.5)' }}>
            <Span variant="ellipsis">
              <Link
                target="_blank"
                variant="xs"
                to={`${links.tripShare}/${
                  original.shipmentNo
                }${'?tab=transporterDetails'}`}
              >
                {t("See Transporter's List")}
              </Link>
            </Span>
          </FullHeight>
        )
      }
    case 'packageId':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        minWidth: 130,
        sortable: false,
        ...columnConfig,
        Cell: ({ row: { original } }: any) => (
          <ColFullHeight>
            <Span variant="ellipsis">
              <Link
                target="_blank"
                variant="xs"
                to={`${links.tripShare}/${
                  original.shipmentNo
                }${queryParams.tripShare || ''}`}
              >
                {original.shipmentNo}
              </Link>
            </Span>
          </ColFullHeight>
        )
      }
    case 'packageIdKey':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        minWidth: 130,
        sortable: false,
        ...columnConfig,
        Cell: ({ row: { original } }: any) => (
          <ColFullHeight>
            <Span variant="ellipsis">
              <Link
                target="_blank"
                variant="xs"
                to={`${links.tripShare}/${
                  original.packageId
                }${queryParams.tripShare || ''}`}
              >
                {original.packageId}
              </Link>
            </Span>
          </ColFullHeight>
        )
      }
    case 'containerNo':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        minWidth: 130,
        sortable: false,
        ...columnConfig,
        Cell: ({ row: { original } }: any) => (
          <ColFullHeight>
            <Label
              style={{ fontWeight: 700, fontSize: '14px', color: '#727272' }}
            >
              {original.containerNo}
            </Label>
          </ColFullHeight>
        )
      }
    case 'startAndEndTime':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        sortable: true,
        accessor: 'startDate',
        minWidth: 170,
        Cell: ({ row: { original } }: any) => {
          const datesArray = [
            original?.actStartTime?.time
              ? displayTime(original.actStartTime)
              : '-',
            original?.actEndTime?.time ? displayTime(original.actEndTime) : '-'
          ]
          return (
            <ColFullHeight justifyContentCenter>
              <MultiModelStoryLine story={datesArray} isDate={true} />
            </ColFullHeight>
          )
        }
      }
    case 'landmark':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        sortable: true,
        accessor: 'landmark',
        minWidth: 170,
        Cell: ({ row: { original } }: any) => {
          return (
            <ColFullHeight justifyContentCenter>
              <Span variant="ellipsis">
                {original?.latestJourney?.latestLocation?.landmark || 'N/A'}
              </Span>
            </ColFullHeight>
          )
        }
      }
    case 'transporterNames':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        sortable: true,
        accessor: 'transporterNames',
        minWidth: 170,
        Cell: ({ row: { original } }: any) => {
          return (
            <ColFullHeight justifyContentCenter>
              <Span variant="ellipsis">
                {original.transporterNames
                  ? original.transporterNames?.length
                  : '-'}
              </Span>
            </ColFullHeight>
          )
        }
      }
    case 'transportModes':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        sortable: true,
        accessor: 'transportModes',
        minWidth: 170,
        Cell: ({ row: { original } }: any) => {
          return (
            <ColFullHeight justifyContentCenter>
              <Span variant="ellipsis">
                {original.transportModes
                  ? t(
                      original.transportModes
                        .map((mode: any) => t(mode))
                        .join(' , ')
                    ) || 'N/A'
                  : '-'}
              </Span>
            </ColFullHeight>
          )
        }
      }
    case 'transshipment':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        sortable: true,
        accessor: 'transshipment',
        minWidth: 170,
        Cell: ({ row: { original } }: any) => {
          return (
            <ColFullHeight justifyContentCenter>
              <Span variant="ellipsis">
                {!isEmpty(original.transshipment)
                  ? original.transshipment?.join(' , ') || 'N/A'
                  : 'Direct'}
              </Span>
            </ColFullHeight>
          )
        }
      }
    case 'shipmentLandmark':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        sortable: true,
        accessor: 'landmark',
        minWidth: 170,
        Cell: ({ row: { original } }: any) => {
          return (
            <ColFullHeight justifyContentCenter>
              <Span variant="ellipsis">{original?.lastLocation?.landmark}</Span>
            </ColFullHeight>
          )
        }
      }
    case 'updatedAt':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        sortable: true,
        accessor: 'updatedAt',
        minWidth: 170,
        Cell: ({ row: { original } }: any) => {
          return (
            <ColFullHeight justifyContentCenter>
              <Span variant="ellipsis">
                {displayTime(
                  original?.latestJourney?.latestLocation?.gpsTimestamp
                )}
              </Span>
            </ColFullHeight>
          )
        }
      }
    case 'shipmentUpdatedAt':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        sortable: true,
        accessor: 'updatedAt',
        minWidth: 170,
        Cell: ({ row: { original } }: any) => {
          return (
            <ColFullHeight justifyContentCenter>
              <Span variant="ellipsis">
                {displayTime(original?.lastLocation?.gpsTimestamp)}
              </Span>
            </ColFullHeight>
          )
        }
      }
    case 'origin':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        sortable: true,
        accessor: 'origin',
        minWidth: 170,
        Cell: ({ row: { original } }: any) => {
          return (
            <ColFullHeight justifyContentCenter>
              <Span variant="ellipsis">
                {original?.startLocation?.locationName}
              </Span>
            </ColFullHeight>
          )
        }
      }
    case 'routeState':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        sortable: true,
        accessor: 'routeState',
        minWidth: 170,
        Cell: ({ row: { original } }: any) => {
          return (
            <ColFullHeight justifyContentCenter>
              <ColorSpan
                title={original.routeState}
                variant="secondary"
                style={{ fontWeight: 600 }}
              >
                {t(toTileCase(original.routeState)) || '-'}
              </ColorSpan>
            </ColFullHeight>
          )
        }
      }
    case 'status':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        sortable: true,
        accessor: 'status',
        minWidth: 170,
        Cell: ({ row: { original } }: any) => {
          return (
            <ColFullHeight justifyContentCenter>
              <ColorSpan
                title={original.status}
                variant="secondary"
                style={{ fontWeight: 600 }}
              >
                {t(toTileCase(original.status)) || '-'}
              </ColorSpan>
            </ColFullHeight>
          )
        }
      }
    case 'destination':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        sortable: true,
        accessor: 'destination',
        minWidth: 170,
        Cell: ({ row: { original } }: any) => {
          return (
            <ColFullHeight justifyContentCenter>
              <Span variant="ellipsis">
                {original?.endLocation?.locationName}
              </Span>
            </ColFullHeight>
          )
        }
      }
    case 'shipmentId':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        minWidth: 160,
        sortable: false,
        ...columnConfig,
        Cell: ({ row: { original } }: any) => (
          <ColFullHeight>
            <Span variant="ellipsis">
              <Link
                target="_blank"
                variant="xs"
                to={`${links.tripShare}/${
                  original.slug
                }${queryParams.tripShare || ''}`}
              >
                {original.lrNo}
              </Link>
            </Span>
          </ColFullHeight>
        )
      }
    default:
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        minWidth: 140,
        ...columnConfig,
        Cell: ({ row: { original } }: any) => {
          let value = getValueFromNestedKey(original, key)
          return (
            <ColFullHeight>
              <Span variant="ellipsis">
                {formatter && formatters[formatter]
                  ? formatters[formatter](value) || '-'
                  : value ?? '-'}
              </Span>
            </ColFullHeight>
          )
        }
      }
  }
}
export function getTableColumns(
  reqdColumns: IColumn[],
  { t, links, queryParams = {} }: any
) {
  const result = reqdColumns.map(ele =>
    generateColumnObject(ele, { t, links, queryParams })
  )
  return result
}
